/*
 * @Description: 运单数据明细
 * @Author: yuanmc
 * @Date: 2025-10-29 15:30:00
 * @LastEditTime: 2025-10-29 15:30:00
 * @LastEditors: yuanmc
 */

import { getWaybillDataList } from '@/api/waybillAI';
import { formatDate } from '@/utils/date';
import { getTenantCodeByBusinessId, getCurrentBusinessId } from '@/config/waybill-data-whitelist';

const storeModuleName = 'waybillDataDetailModule';

const state = {
  // 筛选项
  parameters: [
    {
      style: 'date-picker',
      param: 'sendTimeRange',
      params: ['loadingTimeStart', 'loadingTimeEnd'], // 导出时使用的参数名
      value: [],
      valueType: 'array',
      label: '发货时间',
      type: 'daterange',
      placeholder: '开始日期-结束日期',
      valueFormat: 'yyyy-MM-dd',
      clearable: true,
      size: 'small',
    },
    {
      style: 'date-picker',
      param: 'arriveTimeRange',
      params: ['unloadingTimeStart', 'unloadingTimeEnd'], // 导出时使用的参数名
      value: [],
      valueType: 'array',
      label: '到达时间',
      type: 'daterange',
      placeholder: '开始日期-结束日期',
      valueFormat: 'yyyy-MM-dd',
      clearable: true,
      size: 'small',
    },
    {
      style: 'casccader1',
      param: 'sendAddress',
      params: ['sendProvince', 'sendCity', 'sendDistrict'], // 导出时使用的参数名（按级联顺序）
      value: [],
      valueType: 'array',
      state: 'cityOptions',
      label: '装货地点',
      placeholder: '请选择装货地点',
      clearable: true,
      size: 'small',
    },
    {
      style: 'casccader1',
      param: 'receiveAddress',
      params: ['receiveProvince', 'receiveCity', 'receiveDistrict'], // 导出时使用的参数名（按级联顺序）
      value: [],
      valueType: 'array',
      state: 'cityOptions',
      label: '卸货地点',
      placeholder: '请选择卸货地点',
      clearable: true,
      size: 'small',
    },
    {
      style: 'input',
      param: 'goodsName',
      value: '',
      valueType: 'string',
      label: '货物名称',
      placeholder: '请输入货物名称',
      clearable: true,
    },
    {
      style: 'select',
      param: 'packingMethod',
      exportParam: 'goodsPacking', // 导出时使用的参数名
      value: '',
      valueType: 'string',
      state: 'packingMethodOptions',
      label: '货物包装方式',
      placeholder: '请选择',
      clearable: true,
      filterable: false,
    },
    {
      style: 'select',
      param: 'goodsType',
      exportParam: 'goodsCategory', // 导出时使用的参数名
      value: '',
      valueType: 'string',
      state: 'goodsTypeOptions',
      label: '货物类型',
      placeholder: '请选择',
      clearable: true,
      filterable: false,
    },
    {
      style: 'select',
      param: 'vehicleType',
      value: '',
      valueType: 'string',
      state: 'vehicleTypeOptions',
      label: '所需车辆类型',
      placeholder: '请选择',
      clearable: true,
      filterable: false,
    },
  ],
  // 导出配置（使用自定义导出按钮，以便精确控制导出参数，不带分页参数）
  isExport: {
    isShow: false, // 隐藏默认导出按钮，使用 index.vue 中的自定义导出
  },
  // 页码 + 参数
  postParams: {
    page: 1,
    size: 20,
  },
  // 列表数据
  listData: {
    list: [],
    tableHeadList: [
      {
        label: '序号',
        prop: 'index',
        width: 40,
        checked: 1,
        type: 'index',
      },
      {
        label: '发货时间',
        prop: 'sendTime',
        width: 150,
        checked: 1,
      },
      {
        label: '到达时间',
        prop: 'arriveTime',
        width: 180,
        checked: 1,
      },
      {
        label: '装货地',
        prop: 'sendCity',
        width: 120,
        checked: 1,
        slot: true,
        slotName: 'sendCity',
      },
      {
        label: '卸货地',
        prop: 'receiveCity',
        width: 120,
        checked: 1,
        slot: true,
        slotName: 'receiveCity',
      },
      {
        label: '货物名称',
        prop: 'goodsName',
        width: 120,
        checked: 1,
      },
      {
        label: '包装方式',
        prop: 'packingMethod',
        width: 100,
        checked: 1,
      },
      {
        label: '货物类型',
        prop: 'goodsType',
        width: 100,
        checked: 1,
      },
      {
        label: '重量(吨)',
        prop: 'weight',
        width: 100,
        checked: 1,
      },
      {
        label: '车辆类型',
        prop: 'vehicleType',
        width: 120,
        checked: 1,
      },
      {
        label: '联系人',
        prop: 'contactName',
        width: 100,
        checked: 1,
      },
      {
        label: '电话',
        prop: 'contactPhone',
        width: 120,
        checked: 1,
      },
    ],
    total: 0,
  },
  // 选项数据
  packingMethodOptions: [],
  goodsTypeOptions: [],
  vehicleTypeOptions: [],
  cityOptions: [], // 省市区数据
};

const mutations = {
  updateValueFromIndex(state, { index, value }) {
    state.parameters[index].value = value;
  },
  updateParams(state, params) {
    state.postParams = { ...state.postParams, ...params };
  },
  // nf-table 组件需要的 mutation（处理分页）
  updatePostParams(state, params) {
    state.postParams = { ...state.postParams, ...params };
  },
  updateListData(state, data) {
    // 时间戳转换辅助函数
    const formatTimestamp = timestamp => {
      if (!timestamp) return '';
      // 如果是字符串，转换为数字
      const time = typeof timestamp === 'string' ? Number(timestamp) : timestamp;
      return formatDate(time, 'yyyy-MM-dd hh:mm:ss');
    };

    // 拼接详细地址的辅助函数
    const buildDetailAddress = (province, city, district, address) => {
      // 如果有详细地址，只显示详细地址
      if (address && address.trim() !== '') {
        return address;
      }

      // 如果没有详细地址，显示省市区
      const parts = [];
      if (province) parts.push(province);
      if (city && city !== province) parts.push(city); // 避免重复（如直辖市）
      if (district) parts.push(district);
      return parts.join(' ');
    };

    // 对返回的数据进行字段映射
    const mappedList = (data.list || []).map((item, idx) => ({
      ...item,
      index: (state.postParams.page - 1) * state.postParams.size + idx + 1, // 计算序号
      // 格式化时间字段
      createTime: formatTimestamp(item.createTime), // 创建时间
      updateTime: formatTimestamp(item.updateTime), // 更新时间
      sendTime: formatTimestamp(item.loadingTime), // 发货时间（表格显示字段）
      arriveTime: formatTimestamp(item.unloadingTime), // 到达时间（表格显示字段）
      packingMethod: item.goodsPacking, // 货物包装 → 包装方式
      goodsType: item.goodsCategory, // 货物类别 → 货物类型
      weight: item.sendQuantity, // 发货数量 → 重量（需要根据实际业务调整）
      sendCity: item.sendCity || item.sendProvince, // 装货地（优先市，否则省）
      receiveCity: item.receiveCity || item.receiveProvince, // 卸货地（优先市，否则省）
      // 详细地址（用于 tooltip 显示）
      sendAddressDetail: buildDetailAddress(
        item.sendProvince,
        item.sendCity,
        item.sendDistrict,
        item.sendAddress,
      ),
      receiveAddressDetail: buildDetailAddress(
        item.receiveProvince,
        item.receiveCity,
        item.receiveDistrict,
        item.receiveAddress,
      ),
    }));

    state.listData.list = mappedList;
    state.listData.total = data.total || 0;
  },
  updateOptions(state, { packingMethodOptions, goodsTypeOptions, vehicleTypeOptions }) {
    if (packingMethodOptions) {
      state.packingMethodOptions = packingMethodOptions;
    }
    if (goodsTypeOptions) {
      state.goodsTypeOptions = goodsTypeOptions;
    }
    if (vehicleTypeOptions) {
      state.vehicleTypeOptions = vehicleTypeOptions;
    }
  },
  updateCityOptions(state, cityOptions) {
    state.cityOptions = cityOptions;
  },
  resetParams(state) {
    state.parameters.forEach(item => {
      if (item.valueType === 'array') {
        item.value = [];
      } else {
        item.value = '';
      }
    });
    state.postParams.page = 1;
  },
};

const actions = {
  // 构建查询/导出参数（公共方法）
  buildParams({ state }, options = {}) {
    // 根据当前企业ID获取对应的租户Code
    const currentBusinessId = getCurrentBusinessId();
    const tenantCode = getTenantCodeByBusinessId(currentBusinessId);

    const { includePageParams = true } = options;
    const params = {
      thirdTenantCode: tenantCode,
    };

    // 是否包含分页参数
    if (includePageParams) {
      params.page = state.postParams.page;
      params.size = state.postParams.size;
    }

    // 处理筛选参数
    state.parameters.forEach(item => {
      if (item.param === 'sendTimeRange' && item.value && item.value.length === 2) {
        params.loadingTimeStart = `${item.value[0]} 00:00:00`;
        params.loadingTimeEnd = `${item.value[1]} 23:59:59`;
      } else if (item.param === 'arriveTimeRange' && item.value && item.value.length === 2) {
        params.unloadingTimeStart = `${item.value[0]} 00:00:00`;
        params.unloadingTimeEnd = `${item.value[1]} 23:59:59`;
      } else if (item.param === 'sendAddress' && item.value && item.value.length > 0) {
        // 根据选择的层级设置省市区
        if (item.value.length >= 1) params.sendProvince = item.value[0];
        if (item.value.length >= 2) params.sendCity = item.value[1];
        if (item.value.length >= 3) params.sendDistrict = item.value[2];
      } else if (item.param === 'receiveAddress' && item.value && item.value.length > 0) {
        // 根据选择的层级设置省市区
        if (item.value.length >= 1) params.receiveProvince = item.value[0];
        if (item.value.length >= 2) params.receiveCity = item.value[1];
        if (item.value.length >= 3) params.receiveDistrict = item.value[2];
      } else if (item.param === 'packingMethod' && item.value) {
        params.goodsPacking = item.value;
      } else if (item.param === 'goodsType' && item.value) {
        params.goodsCategory = item.value;
      } else if (item.value && item.param !== 'sendTimeRange' && item.param !== 'arriveTimeRange') {
        params[item.param] = item.value;
      }
    });

    return params;
  },

  async getList({ dispatch, commit }) {
    try {
      // 构建查询参数（包含分页）
      const params = await dispatch('buildParams', { includePageParams: true });
      const { code, data } = await getWaybillDataList(params);
      if (code === '00000' || code === 200) {
        commit('updateListData', data);
        return data;
      }
    } catch (error) {
      console.error('获取运单数据列表失败:', error);
      throw error;
    }
  },

  // 获取导出参数（不包含分页）
  async getExportParams({ dispatch }) {
    return await dispatch('buildParams', { includePageParams: false });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
